// Client helpers for the pickup-code + handoff flow.
// Wraps fetch() with apiBaseUrl() so dev (port 3500) and prod both work.

import { apiBaseUrl } from './api';

/**
 * Fetch the pickup code for a transaction from /api/pickup-code.
 *
 * Caller MUST be the customer on the transaction and the tx MUST be on
 * a v2+ process — the endpoint returns UNSUPPORTED_PROCESS for v1.
 * Gate this call in the UI on processVersion >= 2 to avoid spamming
 * the endpoint.
 *
 * @param {string} transactionId
 * @returns {Promise<{ code: string }>} on success
 * @throws {{ status, error, message }} on any non-2xx response
 */
export const fetchPickupCode = async transactionId => {
  const res = await fetch(
    `${apiBaseUrl()}/api/pickup-code?transactionId=${encodeURIComponent(transactionId)}`,
    { credentials: 'include' }
  );
  let body;
  try {
    body = await res.json();
  } catch (e) {
    body = {};
  }
  if (!res.ok) {
    const err = new Error(body.message || `HTTP ${res.status}`);
    err.status = res.status;
    err.code = body.error;
    err.body = body;
    throw err;
  }
  return body;
};

/**
 * Fetch the host's exact pickup address for a transaction from
 * /api/pickup-address. The exact address lives in listing privateData
 * (2026-07-02 privacy split); the endpoint gates on participation and,
 * for renters, on the booking being committed (accepted / paid).
 * Callers should treat a 403 as "not revealed yet" and fall back to
 * the public city-level location.
 *
 * @param {string} transactionId
 * @returns {Promise<{ address: string, building: string }>}
 */
export const fetchPickupAddress = async transactionId => {
  const res = await fetch(
    `${apiBaseUrl()}/api/pickup-address?transactionId=${encodeURIComponent(transactionId)}`,
    { credentials: 'include' }
  );
  let body;
  try {
    body = await res.json();
  } catch (e) {
    body = {};
  }
  if (!res.ok) {
    const err = new Error(body.message || `HTTP ${res.status}`);
    err.status = res.status;
    err.code = body.error;
    err.body = body;
    throw err;
  }
  return body;
};

/**
 * Post a handoff transition via /api/transition-privileged. Used for
 * confirm-pickup, report-pickup-issue, complete-return, report-damage.
 *
 * @param {object} params
 * @param {string} params.transactionId
 * @param {string} params.transition  e.g. 'transition/confirm-pickup'
 * @param {object} params.orderData   Transition-specific payload (photos, code, description, etc.)
 * @returns {Promise<object>} the Sharetribe response data on success
 * @throws {{ status, error, message }} on any failure
 */
export const postHandoffTransition = async ({ transactionId, transition, orderData }) => {
  const res = await fetch(`${apiBaseUrl()}/api/transition-privileged`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      isSpeculative: false,
      orderData: orderData || {},
      bodyParams: {
        id: transactionId,
        transition,
        params: {},
      },
      queryParams: { expand: true, include: ['booking', 'listing'] },
    }),
  });
  const text = await res.text();
  // The server serializes responses as application/transit+json with a
  // nested { status, statusText, data } envelope. We don't need the
  // inner data for the UI flows here — a 2xx is enough to proceed.
  if (!res.ok) {
    let err;
    try {
      err = JSON.parse(text);
    } catch (e) {
      err = { error: 'UNKNOWN', message: text || `HTTP ${res.status}` };
    }
    // Server's errorbody shape is { errors: [{ status, code, message }] }.
    const firstErr = err?.errors?.[0] || {};
    const e = new Error(firstErr.message || err.message || `HTTP ${res.status}`);
    e.status = res.status;
    e.code = firstErr.code || err.error;
    e.body = err;
    throw e;
  }
  return text;
};
